// Esqueletos de las pantallas de la tienda: lo que se ve mientras llegan los datos, con la forma y la medida de lo que
// va a aparecer (tarjetas de producto del mismo tamaño que TarjetaProducto, filas del carrito, pedidos, mensajes), así
// nada salta al llegar. Usan las primitivas de @reparto/ui: bloques con un brillo que se desliza (quietos con
// prefers-reduced-motion); en la tienda el bloque es un velo translúcido que se ve tanto sobre la superficie blanca como
// sobre el fondo gris de las páginas (estilos.css). La tienda no aplica `dark:`: da vuelta los tonos stone con variables,
// así los mismos bloques y marcos sirven en modo claro y oscuro. La zona espera 150 ms antes de mostrarse
// (ZonaCargando): con los datos guardados en el celular no aparece nada.
// Los altos de los renglones son los del texto de verdad: la letra base es de 17 px con interlineado 1,5 (25,5 px), la
// de 15 px ocupa 22,5 px y los títulos de 28 px con leading-tight, 35 px.
import { cx, Esqueleto, EsqueletoTarjeta, ZonaCargando } from '@reparto/ui';
import type { ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Cargando } from './base';
import { Pantalla } from './Marco';
import { CLASES_EN_CARRUSEL, CLASES_GRILLA } from './TarjetaProducto';

/** Anchos de los nombres, para que no queden todos iguales. */
const ANCHOS = ['72%', '58%', '84%', '64%', '78%', '52%'];
const ancho = (i: number) => ANCHOS[i % ANCHOS.length]!;

/** Un renglón de texto en gris: el alto del renglón (`alto`, el interlineado del texto) y la barra adentro. */
function Renglon({
  alto = 'h-5',
  barra = 'h-3',
  ancho = '100%',
  className,
}: {
  alto?: string;
  barra?: string;
  /** En CSS: '60%' o '8rem' (dentro de algo centrado, en rem: el porcentaje no tiene de qué medirse). */
  ancho?: string;
  className?: string;
}) {
  return (
    <div className={cx('flex items-center', alto, className)}>
      <Esqueleto className={cx(barra, 'rounded-full')} style={{ width: ancho }} />
    </div>
  );
}

/** Un ícono que todavía no está: un cuadrito redondeado del tamaño del ícono. */
function IconoGris({ className }: { className: string }) {
  return <Esqueleto className={cx('shrink-0 rounded-lg', className)} />;
}

// ---------- Producto ----------

/**
 * Una tarjeta de producto en gris, del mismo tamaño que TarjetaProducto: el cuadro de la foto, dos renglones de nombre,
 * la presentación, el precio con su unidad (y, en "Lo de siempre", la cantidad de la última vez) y el botón "Agregar".
 */
export function EsqueletoTarjetaProducto({ i = 0, frecuente }: { i?: number; frecuente?: boolean }) {
  return (
    <div className="flex h-full w-full flex-col rounded-2xl bg-white p-2 shadow-sm ring-1 ring-stone-200">
      <Esqueleto className="aspect-square w-full rounded-xl" />
      <div className="px-1 pt-2">
        <Renglon barra="h-3.5" ancho="94%" />
        <Renglon barra="h-3.5" ancho={ancho(i)} />
        {i % 3 === 2 ? <div className="h-5" /> : <Renglon barra="h-2.5" ancho="42%" />}
      </div>
      <div className="px-1 pt-1">
        <Renglon alto="h-6" barra="h-4" ancho="62%" />
        <Renglon barra="h-2.5" ancho="36%" />
        {frecuente ? <Renglon barra="h-2.5" ancho="74%" /> : null}
      </div>
      <div className="mt-auto pt-2">
        <Esqueleto className="h-10 w-full rounded-full" />
      </div>
    </div>
  );
}

/** El título de una fila del inicio (TituloFila): el ícono, el nombre y debajo cuántos productos. */
function TituloFilaGris({ icono = true, ancho = '9rem' }: { icono?: boolean; ancho?: string }) {
  return (
    <div className="flex min-w-0 items-center gap-2.5">
      {icono ? <IconoGris className="h-6.5 w-6.5" /> : null}
      <div className="min-w-0">
        <Renglon alto="h-6" barra="h-4.5" ancho={ancho} />
        <Renglon className="mt-0.5" ancho="5.5rem" />
      </div>
    </div>
  );
}

/** "Ver todos →" de cada fila. */
function VerTodosGris() {
  return (
    <div className="flex min-h-11 shrink-0 items-center px-1">
      <Esqueleto className="h-3 w-20 rounded-full" />
    </div>
  );
}

/** Una fila que se desliza (Carrusel): el título, "Ver todos" (y las flechas desde tablet) y las tarjetas. */
function FilaProductos({
  tarjetas = 6,
  icono = true,
  titulo,
  frecuente,
}: {
  tarjetas?: number;
  icono?: boolean;
  titulo?: string;
  frecuente?: boolean;
}) {
  return (
    <div>
      <div className="mb-3 flex min-h-11 items-center justify-between gap-3">
        <TituloFilaGris icono={icono} ancho={titulo} />
        <div className="flex shrink-0 items-center gap-2">
          <VerTodosGris />
          <Esqueleto className="hidden h-10 w-10 rounded-full md:block" />
          <Esqueleto className="hidden h-10 w-10 rounded-full md:block" />
        </div>
      </div>
      <div className="-mx-4 flex gap-3 overflow-hidden px-4 pt-1 pb-2 md:-mx-1 md:px-1">
        {Array.from({ length: tarjetas }, (_, i) => (
          <div key={i} className={CLASES_EN_CARRUSEL}>
            <EsqueletoTarjetaProducto i={i} frecuente={frecuente} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- Inicio ----------

/** Una tarjeta de "Comprá por categoría": el ícono y el nombre. */
function TarjetaCategoriaGris({ i }: { i: number }) {
  return (
    <div className="flex min-h-24 flex-col items-center justify-center gap-2 rounded-2xl border border-stone-200 bg-white px-2 py-3 shadow-sm">
      <IconoGris className="h-7.5 w-7.5" />
      <Renglon alto="h-[18.75px]" ancho={['4.5rem', '3.5rem', '5rem'][i % 3]} />
    </div>
  );
}

/** "Comprá por categoría" con sus seis tarjetas y dos filas de productos que se deslizan. */
function FormaVitrina() {
  return (
    <>
      <section className="mt-8">
        <div className="flex min-h-11 items-center justify-between gap-3">
          <TituloFilaGris icono={false} ancho="11rem" />
          <VerTodosGris />
        </div>
        <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-6">
          {Array.from({ length: 6 }, (_, i) => (
            <TarjetaCategoriaGris key={i} i={i} />
          ))}
        </div>
        <div className="mt-2 flex min-h-12 items-center justify-center">
          <Esqueleto className="h-3.5 w-52 rounded-full" />
        </div>
      </section>
      <div className="mt-6 space-y-8">
        {['9rem', '7rem'].map((titulo) => (
          <section key={titulo} className="border-t border-stone-200 pt-5">
            <FilaProductos titulo={titulo} />
          </section>
        ))}
      </div>
    </>
  );
}

/** Inicio: las categorías y las filas de productos mientras llega la vitrina. */
export function EsqueletoVitrina() {
  return (
    <ZonaCargando texto="Cargando los productos…">
      <FormaVitrina />
    </ZonaCargando>
  );
}

/**
 * "Lo de siempre" con "Repetir último pedido": en el inicio, una fila que se desliza; en su pantalla, la grilla con
 * "Agregar todo".
 */
function FormaLoDeSiempre({ grilla }: { grilla?: boolean }) {
  return (
    <>
      {grilla ? (
        <>
          <div className="mb-3 flex min-h-11 items-center justify-between gap-3">
            <Renglon alto="h-7" barra="h-4.5" ancho="9rem" />
            <Esqueleto className="h-12 w-40 shrink-0 rounded-full" />
          </div>
          <div className={CLASES_GRILLA}>
            {Array.from({ length: 4 }, (_, i) => (
              <EsqueletoTarjetaProducto key={i} i={i} frecuente />
            ))}
          </div>
        </>
      ) : (
        <FilaProductos tarjetas={4} titulo="8rem" frecuente />
      )}
      <div className="mt-2 flex items-center gap-3 rounded-2xl border border-stone-200 bg-white p-3 shadow-sm">
        <Esqueleto className="h-12 w-12 shrink-0 rounded-xl" />
        <div className="min-w-0 flex-1">
          <Renglon alto="h-[25.5px]" barra="h-3.5" ancho="55%" />
          <Renglon alto="h-[22.5px]" ancho="78%" />
        </div>
        <IconoGris className="h-6 w-6" />
      </div>
    </>
  );
}

export function EsqueletoLoDeSiempre({ grilla }: { grilla?: boolean }) {
  return (
    <ZonaCargando texto="Cargando lo de siempre…" className={grilla ? 'mt-6' : 'mt-8'}>
      <FormaLoDeSiempre grilla={grilla} />
    </ZonaCargando>
  );
}

/** El buscador del catálogo ("Buscar en el catálogo"), del mismo alto. */
function BuscadorGris() {
  return (
    <div className="mt-4 flex min-h-14 items-center gap-2.5 rounded-2xl bg-white px-4 shadow-sm ring-1 ring-stone-200">
      <IconoGris className="h-6 w-6" />
      <Esqueleto className="h-3.5 w-44 rounded-full" />
    </div>
  );
}

// ---------- Catálogo ----------

/** La grilla de productos (categoría y búsqueda): tarjetas en gris del mismo tamaño que las de verdad. */
export function EsqueletoGrilla({ tarjetas = 10, className }: { tarjetas?: number; className?: string }) {
  return (
    <ZonaCargando texto="Cargando los productos…" className={cx(CLASES_GRILLA, className)}>
      {Array.from({ length: tarjetas }, (_, i) => (
        <EsqueletoTarjetaProducto key={i} i={i} />
      ))}
    </ZonaCargando>
  );
}

/** Un chip de categoría (blanco, con borde): el ícono y el nombre. */
function ChipGris({ ancho: a }: { ancho: string }) {
  return (
    <div className="flex h-11 shrink-0 items-center gap-1.5 rounded-full border border-stone-300 bg-white px-4">
      <IconoGris className="h-5 w-5 rounded-md" />
      <Esqueleto className={cx('h-3 rounded-full', a)} />
    </div>
  );
}

const ANCHOS_CHIP = ['w-28', 'w-14', 'w-20', 'w-16', 'w-24', 'w-12', 'w-20'];

/** Los chips de las categorías: se deslizan en el celular y van en varias filas en computadora. */
function ChipsGris() {
  return (
    <div className="-mx-4 mt-4 flex gap-2 overflow-hidden px-4 pb-1 lg:mx-0 lg:flex-wrap lg:px-0">
      {ANCHOS_CHIP.map((a, i) => (
        <ChipGris key={i} ancho={a} />
      ))}
    </div>
  );
}

/** Los chips de las categorías mientras llegan (la grilla de abajo es la que avisa al lector de pantalla). */
export function EsqueletoChips() {
  return (
    <div aria-hidden="true" className="esqueleto-espera">
      <ChipsGris />
    </div>
  );
}

/** Todas las categorías: cuántas hay y las tarjetas con el ícono, el nombre y cuántos productos. */
export function EsqueletoCategorias() {
  return (
    <ZonaCargando texto="Cargando las categorías…">
      <Renglon alto="h-[22.5px]" ancho="7rem" className="mt-3" />
      <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {Array.from({ length: 8 }, (_, i) => (
          <div key={i} className="flex min-h-32 flex-col rounded-2xl border border-stone-200 bg-white p-4 shadow-sm">
            <IconoGris className="mb-3 h-8 w-8" />
            <Renglon alto="h-[25.5px]" barra="h-3.5" ancho={ancho(i)} />
            <div className="mt-1 flex h-[22.5px] items-center justify-between gap-2">
              <Esqueleto className="h-3 w-20 rounded-full" />
              <IconoGris className="h-4 w-4" />
            </div>
          </div>
        ))}
      </div>
    </ZonaCargando>
  );
}

/** La ficha del producto: la foto grande, la categoría, el nombre, la presentación, el precio, el stock y "Agregar". */
export function EsqueletoFicha() {
  return (
    <ZonaCargando
      texto="Cargando el producto…"
      className="mt-3 md:grid md:grid-cols-[minmax(0,26rem)_minmax(0,1fr)] md:items-start md:gap-10"
    >
      <div className="rounded-2xl bg-white p-2 ring-1 ring-stone-200">
        <Esqueleto className="aspect-square max-h-[30dvh] w-full rounded-xl md:max-h-none" />
      </div>
      <div className="mt-5 md:mt-0">
        <Esqueleto className="h-9 w-32 rounded-full" />
        <Renglon alto="h-[32.5px] md:h-10" barra="h-6" ancho="82%" className="mt-3" />
        <Renglon alto="h-[25.5px]" barra="h-3.5" ancho="48%" className="mt-1" />
        <Renglon alto="h-9 md:h-10" barra="h-7" ancho="13rem" className="mt-4" />
        <Renglon alto="h-[22.5px]" ancho="7rem" className="mt-2" />
        <Esqueleto className="mt-5 h-12 w-full max-w-sm rounded-full" />
      </div>
    </ZonaCargando>
  );
}

// ---------- Carrito y pedidos ----------

/**
 * Un producto del carrito (o de "Modificar pedido"): la foto, el nombre con la papelera, el precio por unidad, − cantidad
 * + y el subtotal.
 */
function FilaCarritoGris({ i, miniatura }: { i: number; miniatura?: boolean }) {
  return (
    <div className="rounded-2xl border border-stone-200 bg-white p-3 shadow-sm">
      <div className="flex gap-3">
        <Esqueleto
          className={cx('shrink-0 self-start', miniatura ? 'h-14 w-14 rounded-2xl' : 'aspect-square w-18 rounded-xl')}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-2">
            <Renglon alto="h-[23.375px]" barra="h-3.5" ancho={ancho(i)} className="min-w-0 flex-1" />
            <div className="-mt-1 -mr-1 grid h-11 w-11 shrink-0 place-items-center">
              <IconoGris className="h-6 w-6" />
            </div>
          </div>
          <Renglon alto="h-[22.5px]" ancho="66%" />
          <div className="mt-2 flex items-center justify-between gap-2">
            <Esqueleto className="h-11 w-32 rounded-full" />
            <Esqueleto className="h-4.5 w-24 rounded-full" />
          </div>
        </div>
      </div>
    </div>
  );
}

/** Productos, bultos y el total (Totales del carrito, con renglones de 33,5 px, y del pedido, de 29,5 px). */
function TotalesGris({ pedido, className }: { pedido?: boolean; className?: string }) {
  return (
    <div className={cx('rounded-3xl border border-stone-200 bg-white p-4 shadow-sm', className)}>
      {[0, 1].map((f) => (
        <div key={f} className={cx('flex items-center justify-between', pedido ? 'h-[29.5px]' : 'h-[33.5px]')}>
          <Esqueleto className="h-3.5 w-20 rounded-full" />
          <Esqueleto className="h-3.5 w-6 rounded-full" />
        </div>
      ))}
      <div className="mt-2 flex h-[45px] items-end justify-between border-t border-stone-200 pb-1">
        <Esqueleto className="h-4.5 w-14 rounded-full" />
        <Esqueleto className="h-6 w-36 rounded-full" />
      </div>
    </div>
  );
}

/** Un título de sección (text-xl, 28 px de alto). */
function TituloGris({ ancho: a, className }: { ancho: string; className?: string }) {
  return <Renglon alto="h-7" barra="h-4.5" ancho={a} className={className} />;
}

/** El carrito: los productos, cómo pagar, la entrega, la nota y el total (en computadora, el total al costado). */
export function EsqueletoCarrito() {
  return (
    <ZonaCargando
      texto="Abriendo tu carrito…"
      className="lg:grid lg:grid-cols-[minmax(0,1fr)_22rem] lg:items-start lg:gap-4 lg:px-2"
    >
      <div className="min-w-0 pb-4">
        <div className="flex flex-col gap-3 px-4 pt-4">
          {[0, 1, 2].map((i) => (
            <FilaCarritoGris key={i} i={i} />
          ))}
        </div>
        <div className="mt-2 flex justify-between gap-2 px-4">
          <div className="flex h-12 items-center px-5">
            <Esqueleto className="h-3.5 w-44 rounded-full" />
          </div>
          <div className="flex h-12 items-center px-5">
            <Esqueleto className="h-3.5 w-16 rounded-full" />
          </div>
        </div>
        <section className="px-4 pt-5">
          <TituloGris ancho="12rem" />
          <Renglon alto="h-[22.5px]" ancho="16rem" className="mt-0.5 mb-3" />
          <div className="grid grid-cols-2 gap-2">
            {[0, 1, 2, 3].map((m) => (
              <div
                key={m}
                className="flex min-h-20 flex-col justify-center gap-1.5 rounded-2xl border-2 border-stone-200 bg-white px-3 py-2.5"
              >
                <div className="flex h-[25.5px] items-center gap-2">
                  <IconoGris className="h-6 w-6" />
                  <Esqueleto className="h-3.5 w-20 rounded-full" />
                </div>
                <Esqueleto className="h-3 w-4/5 rounded-full" />
              </div>
            ))}
          </div>
        </section>
        <section className="px-4 pt-5">
          <div className="mb-3 flex min-h-11 items-center">
            <TituloGris ancho="6rem" />
          </div>
          <div className="flex items-center gap-3 rounded-2xl border border-stone-200 bg-white p-4 shadow-sm">
            <IconoGris className="h-7.5 w-7.5" />
            <div className="min-w-0 flex-1">
              <Renglon alto="h-[25.5px]" barra="h-3.5" ancho="60%" />
              <Renglon alto="h-[22.5px]" ancho="45%" />
            </div>
          </div>
        </section>
        <section className="px-4 pt-5">
          <TituloGris ancho="11rem" className="mb-2" />
          <div className="h-[104.5px] rounded-2xl border-2 border-stone-200 bg-white" />
        </section>
        <section className="px-4 pt-5 pb-4 lg:hidden">
          <TotalesGris />
        </section>
      </div>
      <div className="sticky top-22 hidden px-4 pt-4 lg:block">
        <TotalesGris />
        <div className="mt-3 rounded-3xl border border-stone-200 bg-white p-3 shadow-sm">
          <Esqueleto className="h-15 w-full rounded-full" />
        </div>
      </div>
    </ZonaCargando>
  );
}

/** Mis pedidos: cada pedido con su número, el estado, cuándo se hizo, cuándo llega, los bultos y el total. */
export function EsqueletoPedidos({ filas = 4 }: { filas?: number }) {
  return (
    <ZonaCargando texto="Cargando tus pedidos…" className="flex flex-col gap-3">
      {Array.from({ length: filas }, (_, i) => (
        <EsqueletoTarjeta key={i} className="shadow-sm">
          <div className="flex h-7 items-center justify-between gap-2">
            <Esqueleto className="h-4.5 w-24 rounded-full" />
            <Esqueleto className={cx('h-7 rounded-full', i % 2 ? 'w-32' : 'w-28')} />
          </div>
          <Renglon alto="h-[22.5px]" ancho="9rem" />
          <div className="mt-3 flex h-[25.5px] items-center gap-1.5">
            <IconoGris className="h-5.5 w-5.5 rounded-md" />
            <Esqueleto className="h-3.5 w-44 rounded-full" />
          </div>
          <div className="mt-1 flex h-[25.5px] items-center justify-between gap-2">
            <Esqueleto className="h-3 w-36 rounded-full" />
            <div className="flex items-center gap-1">
              <Esqueleto className="h-4 w-24 rounded-full" />
              <IconoGris className="h-6 w-6" />
            </div>
          </div>
        </EsqueletoTarjeta>
      ))}
    </ZonaCargando>
  );
}

/** Un dato con su ícono, la etiqueta y el valor (Te llega, Forma de pago). */
function DatoGris({ valor = '60%', centrado }: { valor?: string; centrado?: boolean }) {
  return (
    <div className={cx('flex gap-3 p-4', centrado ? 'items-center' : 'items-start')}>
      <IconoGris className={centrado ? 'h-7 w-7' : 'mt-0.5 h-6.5 w-6.5'} />
      <div className="min-w-0 flex-1">
        <Renglon alto="h-[22.5px]" ancho="32%" />
        <Renglon alto={centrado ? 'h-7' : 'h-[25.5px]'} barra="h-4" ancho={valor} />
      </div>
    </div>
  );
}

/** Un artículo del pedido: la miniatura, el nombre, la presentación con el precio, la cantidad y el subtotal. */
function ItemGris({ i }: { i: number }) {
  return (
    <div className="flex gap-3 px-4 py-3.5">
      <Esqueleto className="h-12 w-12 shrink-0 rounded-2xl" />
      <div className="min-w-0 flex-1">
        <Renglon alto="h-[23.375px]" barra="h-3.5" ancho={ancho(i)} />
        <Renglon alto="h-[22.5px]" ancho="70%" />
        <div className="mt-1 flex h-[25.5px] items-center justify-between gap-2">
          <Esqueleto className="h-3.5 w-20 rounded-full" />
          <Esqueleto className="h-3.5 w-24 rounded-full" />
        </div>
      </div>
    </div>
  );
}

/** El detalle de un pedido: cómo va con sus cuatro pasos, la entrega y el pago, lo que se pidió, el total y los botones. */
export function EsqueletoPedido() {
  return (
    <ZonaCargando texto="Cargando el pedido…" className="flex flex-col gap-4 px-4 py-4">
      <EsqueletoTarjeta className="shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Esqueleto className="h-9 w-36 rounded-full" />
          <Renglon alto="h-[22.5px]" ancho="12rem" />
        </div>
        <div className="mt-5 grid grid-cols-4">
          {[0, 1, 2, 3].map((n) => (
            <div key={n} className="relative flex flex-col items-center">
              {n > 0 ? (
                <span className="absolute top-[18px] right-1/2 h-1 w-full -translate-y-1/2 bg-stone-200" />
              ) : null}
              <Esqueleto className="relative z-10 h-9 w-9 rounded-full" />
              <Renglon alto="h-[16.25px]" barra="h-2.5" ancho="3.5rem" className="mt-1.5" />
            </div>
          ))}
        </div>
        <div className="mt-4">
          <Renglon alto="h-[25.5px]" barra="h-3.5" ancho="94%" />
          <Renglon alto="h-[25.5px]" barra="h-3.5" ancho="58%" />
        </div>
      </EsqueletoTarjeta>
      <div className="divide-y divide-stone-100 rounded-3xl border border-stone-200 bg-white shadow-sm">
        <DatoGris valor="55%" />
        <DatoGris valor="30%" />
      </div>
      <div>
        <TituloGris ancho="9rem" className="mb-3" />
        <div className="divide-y divide-stone-100 rounded-3xl border border-stone-200 bg-white shadow-sm">
          {[0, 1, 2].map((i) => (
            <ItemGris key={i} i={i} />
          ))}
        </div>
        <TotalesGris pedido className="mt-3" />
      </div>
      <div className="flex flex-col gap-3 pt-1 pb-2">
        <Esqueleto className="h-14 w-full rounded-full" />
        <Esqueleto className="h-14 w-full rounded-full" />
      </div>
    </ZonaCargando>
  );
}

/** "Pedido enviado": el círculo del visto, el título, el número, los datos y los dos botones. */
export function EsqueletoConfirmado() {
  return (
    <ZonaCargando texto="Cargando tu pedido…">
      <div className="flex flex-col items-center">
        <Esqueleto className="h-24 w-24 rounded-full" />
        <Renglon alto="h-[35px]" barra="h-6" ancho="17rem" className="mt-4" />
        <Renglon alto="h-[35px]" barra="h-6" ancho="11rem" />
        <Renglon alto="h-7" barra="h-4" ancho="12rem" className="mt-2" />
      </div>
      <div className="mt-6 divide-y divide-stone-100 rounded-3xl border border-stone-200 bg-white shadow-sm">
        {['55%', '45%', '35%', '30%'].map((v) => (
          <DatoGris key={v} valor={v} centrado />
        ))}
      </div>
      <div className="mt-6 flex flex-col gap-3">
        <Esqueleto className="h-14 w-full rounded-full" />
        <Esqueleto className="h-14 w-full rounded-full" />
      </div>
    </ZonaCargando>
  );
}

/** Un aviso (Aviso) en gris: el ícono y dos renglones. */
function AvisoGris() {
  return (
    <div className="flex gap-3 rounded-2xl border border-stone-200 bg-white p-3.5 shadow-sm">
      <IconoGris className="mt-px h-5.5 w-5.5" />
      <div className="min-w-0 flex-1">
        <Renglon alto="h-[20.625px]" ancho="92%" />
        <Renglon alto="h-[20.625px]" ancho="60%" />
      </div>
    </div>
  );
}

/** "Modificar pedido": el aviso de los precios, los productos con − cantidad + y "Agregar productos". */
export function EsqueletoModificar() {
  return (
    <ZonaCargando texto="Cargando el pedido…">
      <div className="px-4 pt-4">
        <AvisoGris />
      </div>
      <div className="flex flex-col gap-3 px-4 pt-4 pb-4">
        {[0, 1, 2].map((i) => (
          <FilaCarritoGris key={i} i={i} miniatura />
        ))}
        <Esqueleto className="h-12 w-full rounded-full" />
      </div>
    </ZonaCargando>
  );
}

/** Los resultados de "Agregar productos" (la hoja de Modificar pedido): miniatura, nombre, precio y "Agregar". */
export function EsqueletoResultados({ filas = 4 }: { filas?: number }) {
  return (
    <ZonaCargando texto="Buscando productos…" className="divide-y divide-stone-100">
      {Array.from({ length: filas }, (_, i) => (
        <div key={i} className="flex items-center gap-3 py-2.5">
          <Esqueleto className="h-12 w-12 shrink-0 rounded-2xl" />
          <div className="min-w-0 flex-1">
            <Renglon alto="h-[20.625px]" ancho={ancho(i)} />
            <Renglon alto="h-5" barra="h-2.5" ancho="40%" />
          </div>
          <Esqueleto className="h-10 w-36 shrink-0 rounded-full" />
        </div>
      ))}
    </ZonaCargando>
  );
}

// ---------- Mensajes ----------

/** Una burbuja del chat: la de la distribuidora a la izquierda (blanca), la propia a la derecha (del color de la marca). */
function Burbuja({ propio, renglones, ancho: a }: { propio?: boolean; renglones: number; ancho: string }) {
  return (
    <div className={cx('mt-3 flex px-3', propio ? 'justify-end' : 'justify-start')}>
      <div
        className={cx(
          'max-w-[85%] rounded-3xl border px-3 py-2 shadow-sm',
          propio ? 'rounded-tr-lg border-primary-200 bg-primary-50' : 'rounded-tl-lg border-stone-200 bg-white',
        )}
        style={{ width: a }}
      >
        {propio ? null : <Renglon alto="h-5" barra="h-2.5" ancho="50%" className="mb-0.5" />}
        {Array.from({ length: renglones }, (_, r) => (
          <Renglon
            key={r}
            alto="h-[23.375px]"
            barra="h-3.5"
            ancho={renglones > 1 && r === renglones - 1 ? '62%' : '100%'}
          />
        ))}
        <div className="mt-1 flex h-4 items-center justify-end">
          <Esqueleto className="h-2.5 w-10 rounded-full" />
        </div>
      </div>
    </div>
  );
}

/** Mensajes: el día, un aviso de un pedido y burbujas de un lado y del otro. */
export function EsqueletoMensajes() {
  return (
    <ZonaCargando texto="Cargando los mensajes…" className="pt-1 pb-4">
      <div className="my-3 flex justify-center">
        <Esqueleto className="h-7 w-24 rounded-full" />
      </div>
      <div className="my-3 flex justify-center px-4">
        <div className="w-full max-w-md rounded-2xl border border-stone-200 bg-white p-3 shadow-sm">
          <div className="flex items-start gap-2.5">
            <IconoGris className="mt-px h-5.5 w-5.5 rounded-md" />
            <div className="min-w-0 flex-1">
              <Renglon alto="h-[22.5px]" ancho="92%" />
              <Renglon alto="h-[22.5px]" ancho="55%" />
            </div>
            <Esqueleto className="mt-1.5 h-2.5 w-9 shrink-0 rounded-full" />
          </div>
        </div>
      </div>
      <Burbuja renglones={2} ancho="72%" />
      <Burbuja propio renglones={1} ancho="55%" />
      <Burbuja renglones={1} ancho="48%" />
      <Burbuja propio renglones={2} ancho="68%" />
    </ZonaCargando>
  );
}

// ---------- Tu cuenta ----------

/** Una fila con ícono, título, una línea y la flecha (Mensajes, "Cómo se usa la tienda"). */
function FilaEnlaceGris({ sombra = true }: { sombra?: boolean }) {
  return (
    <div
      className={cx('flex items-center gap-3 rounded-3xl border border-stone-200 bg-white p-4', sombra && 'shadow-sm')}
    >
      <IconoGris className="h-7 w-7" />
      <div className="min-w-0 flex-1">
        <Renglon alto="h-7" barra="h-4" ancho="55%" />
        <Renglon alto="h-[22.5px]" ancho="75%" />
      </div>
      <IconoGris className="h-6 w-6" />
    </div>
  );
}

/** Tu cuenta: el negocio con sus datos, con qué se entra, mensajes, la guía, instalar la app y "Salir". */
export function EsqueletoCuenta() {
  return (
    <ZonaCargando texto="Cargando tu cuenta…" className="flex flex-col gap-4 px-4 py-4">
      <EsqueletoTarjeta className="shadow-sm">
        <div className="flex items-start gap-3">
          <Esqueleto className="h-14 w-14 shrink-0 rounded-2xl" />
          <div className="min-w-0 flex-1">
            <Renglon alto="h-[22.5px]" ancho="5.5rem" />
            <Renglon alto="h-[27.5px]" barra="h-4.5" ancho="70%" />
          </div>
        </div>
        <div className="mt-3 divide-y divide-stone-100">
          {['w-36', 'w-24', 'w-28'].map((w) => (
            <div key={w} className="flex h-[45.5px] items-center justify-between gap-4">
              <Esqueleto className="h-3.5 w-20 rounded-full" />
              <Esqueleto className={cx('h-3.5 rounded-full', w)} />
            </div>
          ))}
        </div>
      </EsqueletoTarjeta>
      <EsqueletoTarjeta className="shadow-sm">
        <div className="flex items-center gap-3">
          <IconoGris className="h-9 w-9 rounded-full" />
          <div className="min-w-0 flex-1">
            <Renglon alto="h-[22.5px]" ancho="5rem" />
            <Renglon alto="h-7" barra="h-4" ancho="75%" />
          </div>
        </div>
        <Renglon alto="h-[22.5px]" ancho="65%" className="mt-3" />
      </EsqueletoTarjeta>
      <FilaEnlaceGris sombra={false} />
      <FilaEnlaceGris />
      <EsqueletoTarjeta className="shadow-sm">
        <div className="flex items-start gap-3">
          <IconoGris className="h-7 w-7" />
          <div className="min-w-0 flex-1">
            <Renglon alto="h-7" barra="h-4" ancho="45%" />
            <Renglon alto="h-[22.5px]" ancho="95%" />
            <Renglon alto="h-[22.5px]" ancho="60%" />
          </div>
        </div>
        <Esqueleto className="mt-4 h-14 w-full rounded-full" />
      </EsqueletoTarjeta>
      <Esqueleto className="h-14 w-full rounded-full" />
    </ZonaCargando>
  );
}

// ---------- Guía de uso ----------

/**
 * La guía de uso (/uso) mientras llega su índice: la descripción, el paso elegido, su título y su texto, y la captura
 * del celular (390 × 844).
 */
export function EsqueletoGuia() {
  return (
    <ZonaCargando
      texto="Abriendo la guía…"
      className="lg:grid lg:grid-cols-[16rem_minmax(0,1fr)] lg:items-start lg:gap-8"
    >
      <div className="mb-5 lg:mb-0">
        <Renglon ancho="95%" />
        <Renglon ancho="70%" className="mb-3" />
        <Renglon alto="h-4" barra="h-2.5" ancho="2.5rem" className="mb-1.5" />
        <Esqueleto className="h-12 w-full rounded-2xl" />
      </div>
      <div className="min-w-0">
        <div className="flex h-4 items-center gap-3">
          <Esqueleto className="h-2.5 w-20 rounded-full" />
          <Esqueleto className="h-1.5 max-w-40 flex-1 rounded-full" />
        </div>
        <Renglon alto="h-7 sm:h-8" barra="h-4.5" ancho="60%" className="mt-1.5" />
        <div className="mt-2 max-w-prose">
          <Renglon alto="h-[22.5px]" ancho="100%" />
          <Renglon alto="h-[22.5px]" ancho="85%" />
        </div>
        <Esqueleto className="mx-auto mt-4 aspect-[390/844] w-full max-w-[360px] rounded-2xl md:mx-0" />
      </div>
    </ZonaCargando>
  );
}

// ---------- Pantallas enteras ----------

/**
 * Una pantalla con su marco (la cabecera, el título y la barra de abajo) y el contenido en gris. El subtítulo va vacío
 * pero ocupa su renglón: al llegar los datos el título no se mueve.
 */
function PantallaGris({
  titulo,
  subtitulo = true,
  volver,
  amplia,
  sinNav,
  children,
}: {
  titulo?: string;
  subtitulo?: boolean;
  volver?: string | (() => void);
  amplia?: boolean;
  sinNav?: boolean;
  children: ReactNode;
}) {
  return (
    <Pantalla
      titulo={titulo}
      subtitulo={titulo && subtitulo ? ' ' : undefined}
      volver={volver}
      amplia={amplia}
      sinNav={sinNav}
    >
      {children}
    </Pantalla>
  );
}

/** El inicio entero: el saludo, el buscador, "Lo de siempre" y la vitrina. */
function EsqueletoInicio() {
  return (
    <PantallaGris amplia>
      <ZonaCargando texto="Abriendo la tienda…" className="px-4 pb-8">
        <div className="pt-5">
          <Renglon alto="h-[35px]" barra="h-6" ancho="10rem" />
          <Renglon alto="h-7" barra="h-4" ancho="13rem" className="mt-0.5" />
          <Renglon alto="h-[22.5px]" ancho="16rem" className="mt-1" />
        </div>
        <BuscadorGris />
        <div className="mt-8">
          <FormaLoDeSiempre />
        </div>
        <FormaVitrina />
      </ZonaCargando>
    </PantallaGris>
  );
}

/** Una categoría o la búsqueda: "‹ Volver", el título, el buscador, los chips y la grilla. */
function EsqueletoCatalogo() {
  return (
    <PantallaGris amplia>
      <ZonaCargando texto="Cargando los productos…" className="px-4 pt-4 pb-8">
        <div className="flex min-h-11 items-center">
          <Esqueleto className="h-3.5 w-36 rounded-full" />
        </div>
        <div className="mt-1 flex h-[35px] items-center gap-2.5 md:h-10">
          <IconoGris className="h-8.5 w-8.5" />
          <Esqueleto className="h-6 w-48 rounded-full" />
        </div>
        <BuscadorGris />
        <ChipsGris />
        <div className="mt-5 flex h-7 items-center justify-between gap-3">
          <Esqueleto className="h-4.5 w-28 rounded-full" />
          <Esqueleto className="h-3 w-24 rounded-full" />
        </div>
        <div className={cx(CLASES_GRILLA, 'mt-3')}>
          {Array.from({ length: 6 }, (_, i) => (
            <EsqueletoTarjetaProducto key={i} i={i} />
          ))}
        </div>
      </ZonaCargando>
    </PantallaGris>
  );
}

/** La pantalla de Mensajes mientras se abre (también mientras se descarga su código, que va aparte). */
export function PantallaMensajesCargando() {
  const navegar = useNavigate();
  return (
    <PantallaGris titulo="Mensajes" sinNav volver={() => (window.history.length > 1 ? navegar(-1) : navegar('/'))}>
      <EsqueletoMensajes />
    </PantallaGris>
  );
}

/** "Modificar pedido" mientras se descarga su código (va aparte) o mientras llega la cuenta. */
export function PantallaModificarCargando({ id }: { id?: string }) {
  const params = useParams();
  return (
    <PantallaGris titulo="Modificar pedido" volver={`/pedidos/${id ?? params.id ?? ''}`}>
      <EsqueletoModificar />
    </PantallaGris>
  );
}

/**
 * Lo que se ve al abrir la tienda mientras llega la cuenta (la primera vez en este celular, o al volver de entrar): el
 * esqueleto de la pantalla a la que se va. Elegir el negocio y completar los datos, la ruedita de siempre.
 */
export function PantallaCargando({ ruta }: { ruta: string }) {
  const pedido = /^\/pedidos\/([^/]+)(\/modificar)?$/.exec(ruta);
  if (ruta === '/') return <EsqueletoInicio />;
  if (ruta === '/buscar' || ruta.startsWith('/categoria/')) return <EsqueletoCatalogo />;
  if (ruta === '/categorias')
    return (
      <PantallaGris titulo="Categorías" subtitulo={false} volver="/" amplia>
        <div className="px-4 pb-8">
          <BuscadorGris />
          <EsqueletoCategorias />
        </div>
      </PantallaGris>
    );
  if (ruta.startsWith('/producto/'))
    return (
      <PantallaGris amplia>
        <div className="px-4 pt-4 pb-8">
          <div className="flex min-h-11 items-center">
            <Esqueleto className="h-3.5 w-20 rounded-full" />
          </div>
          <EsqueletoFicha />
        </div>
      </PantallaGris>
    );
  if (ruta === '/carrito')
    return (
      <PantallaGris titulo="Tu carrito" amplia>
        <EsqueletoCarrito />
      </PantallaGris>
    );
  if (ruta === '/lo-de-siempre')
    return (
      <PantallaGris titulo="Lo de siempre" volver="/" amplia>
        <div className="px-4 pb-8">
          <EsqueletoLoDeSiempre grilla />
        </div>
      </PantallaGris>
    );
  if (ruta === '/pedidos')
    return (
      <PantallaGris titulo="Mis pedidos">
        <div className="px-4 py-4">
          <EsqueletoPedidos />
        </div>
      </PantallaGris>
    );
  if (pedido?.[2]) return <PantallaModificarCargando id={pedido[1]!} />;
  if (pedido)
    return (
      <PantallaGris titulo="Pedido" volver="/pedidos">
        <EsqueletoPedido />
      </PantallaGris>
    );
  if (ruta.startsWith('/confirmado/'))
    return (
      <PantallaGris titulo="Pedido enviado" subtitulo={false}>
        <div className="px-4 pt-8 pb-6">
          <EsqueletoConfirmado />
        </div>
      </PantallaGris>
    );
  if (ruta === '/mensajes') return <PantallaMensajesCargando />;
  if (ruta === '/perfil')
    return (
      <PantallaGris titulo="Tu cuenta">
        <EsqueletoCuenta />
      </PantallaGris>
    );
  return <Cargando texto="Abriendo la tienda…" />;
}
